import { AppRoutes } from '../../common/constants';
import { Card, CardContent } from '@mui/material';
import { displayBoolean } from '../../common/helpers/displayBoolean';
import { FNADetails, FNASuretyshipNeeds } from '../../common/types/analyses';
import { formatAmount } from '../../common/components/dataGrid/dataGridEditNumberCell';
import { JSX } from 'react';
import { PolicyCardSectionProps } from '../../common/components/policyCard/policyCardSection';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Content from '../../common/components/content';
import FnaPageHeaderSummary from '../components/fnaPageHeaderSummary';
import InlineError from '../../common/components/inlineError';
import Layout from '../../common/components/layout';
import LayoutLoadingState from '../../common/components/layout/layoutLoadingState';
import PolicyCard from '../../common/components/policyCard';
import PolicyCardsContainer from '../../common/components/policyCard/policyCardsContainer';
import StepFooter from '../../common/components/stepFooter';

/**
 * Display the FNA suretyship needs results (calculations)
 */
const FnaSuretyshipNeedPage = (): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: fna } = useQuery<FNADetails>({
    queryKey: [`/FNADetail/${id}`],
    enabled: Boolean(id),
  });

  const {
    data: suretyshipNeeds,
    error,
    isLoading,
  } = useQuery<FNASuretyshipNeeds>({
    queryKey: [`/FNASuretyshipNeeds/${id}`],
    enabled: Boolean(id),
    retry: false, // disable retrying to get an error immediately
  });

  if (!fna || isLoading) {
    return <LayoutLoadingState />;
  }

  const fnaRoleplayers =
    fna.roleplayers.map(({ roleplayerName }) => roleplayerName).join(', ') || '-';
  const breadcrumbs = [
    { label: 'Financial needs analyses', href: AppRoutes.FNAs },
    { label: fnaRoleplayers, href: `${AppRoutes.FNAs}/${fna.id}` },
    {
      label: 'Contingent Liability (Suretyship protection)',
      href: `${AppRoutes.FNAs}/${fna.id}/suretyship`,
    },
  ];

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        {error ? (
          <InlineError
            error={error}
            actionLabel="Go back"
            actionUrl={`${AppRoutes.FNAs}/${fna.id}/suretyship`}
          />
        ) : (
          <>
            <FnaPageHeaderSummary
              fna={fna}
              title="Contingent Liability (Suretyship protection) needs"
            />
            <Card className="mt-12">
              <CardContent>
                <PolicyCardsContainer>
                  {suretyshipNeeds?.needs.map((need, index) => (
                    <PolicyCard
                      key={need.id}
                      title={`Need ${index + 1}`}
                      sections={[
                        {
                          caption: 'Insured life',
                          title: need.representativeRoleplayerName || need.partyRoleplayerName,
                          items: [
                            need.representativeRoleplayerName && {
                              name: 'Representative life for',
                              value: need.partyRoleplayerName,
                            },
                            {
                              name: 'Tax status',
                              value:
                                need.representativeRoleplayerTaxStatusNote ||
                                need.partyRoleplayerTaxStatusNote,
                            },
                            {
                              name: 'ID number',
                              value:
                                need.representativeRoleplayerIDReg || need.partyRoleplayerIDReg,
                            },
                          ].filter(Boolean) as PolicyCardSectionProps['items'],
                        },
                        {
                          caption: 'Needs',
                          items: [
                            {
                              name: 'Total Suretyship',
                              value: formatAmount(need.totalSuretyship),
                            },
                            {
                              name: 'Net existing provision',
                              value: formatAmount(need.netExistingProvision),
                            },
                            { name: 'Net surety', value: formatAmount(need.netSurety) },
                            {
                              name: 'Provide for estate duty',
                              value: `${formatAmount(need.estateDuty)} (${need.estateDutyRate}%)`,
                            },
                            {
                              name: 'Include disability benefits',
                              value: `${displayBoolean(need.disabilityBenefit)} ${
                                need.disabilityBenefit && need.disabilityBenefitAmount
                                  ? `(${formatAmount(need.disabilityBenefitAmount)})`
                                  : ''
                              }`,
                            },
                          ],
                        },
                        {
                          caption: 'Cover required',
                          items: [{ value: formatAmount(need.coverRequired), large: true }],
                        },
                      ]}
                    />
                  ))}
                </PolicyCardsContainer>
              </CardContent>
              <StepFooter
                isLoading={false}
                nextButtonTitle="Done"
                onClickNext={(): void => navigate(AppRoutes.FNAs)}
                onClickBack={(): void => navigate(`${AppRoutes.FNAs}/${fna.id}/suretyship`)}
              />
            </Card>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default FnaSuretyshipNeedPage;
